import React from 'react';
import { flushSync } from 'react-dom';

import styles from './image.module.scss';

const MASK_SHAPES = {
  arrow: [['M', 0, 0], ['L', 0.50001, 0], ['L', 1, 0.50001], ['L', 0.50001, 1], ['L', 0, 1], ['L', 0.50001, 0.49999], ['Z']],
  circle: [['M', 1, 0.5], ['A', 0.50001, 0.50002, 0, 1, 1, 0.50001, 0], ['A', 0.49999, 0.5, 0, 0, 1, 1, 0.5], ['Z']],
  diamond: [['M', 0, 0.50001], ['L', 0.50718, 1], ['L', 1, 0.50001], ['L', 0.50718, 0], ['Z']],
  egg: [['M', 1, 0.62501], ['C', 1, 0.86664, 0.77614, 1, 0.5, 1], ['C', 0.22386, 1, 0, 0.86664, 0, 0.62501], ['C', 0, 0.38338, 0.22383, 0, 0.5, 0], ['C', 0.77617, 0, 1, 0.38338, 1, 0.62501], ['Z']],
  extended_arrow: [['M', 0.00002, 0.7412], ['L', 0.50152, 0.7412], ['L', 0.50152, 1], ['L', 1, 0.49997], ['L', 0.50152, 0], ['L', 0.50152, 0.2588], ['L', 0.00002, 0.2588], ['Z']],
  heart: [['M', 0.92618, 0.08614], ['A', 0.2518, 0.29413, 0, 0, 0, 0.5701, 0.08614], ['L', 0.49992, 0.16812], ['L', 0.42975, 0.08614], ['A', 0.25178, 0.29411, 0, 0, 0, 0.07366, 0.50209], ['L', 0.14384, 0.58406], ['L', 0.49992, 1], ['L', 0.85601, 0.58406], ['L', 0.92618, 0.50209], ['A', 0.2518, 0.29413, 0, 0, 0, 0.92618, 0.08614], ['Z']],
  hexagon: [['M', 0.75001, 0], ['L', 0.25, 0], ['L', 0.00007, 0.49996], ['L', 0.25, 0.99992], ['L', 0.75001, 0.99992], ['L', 0.99995, 0.49996], ['Z']],
  moon: [['M', 1, 0.92581], ['A', 0.656, 0.50001, 0, 1, 1, 1, 0.07436], ['A', 0.56194, 0.42832, 0, 0, 0, 1, 0.92581], ['Z']],
  octagon: [['M', 0.7071, 0], ['L', 0.29289, 0], ['L', 0, 0.29288], ['L', 0, 0.70708], ['L', 0.29289, 1], ['L', 0.7071, 1], ['L', 0.99998, 0.70708], ['L', 0.99998, 0.29292], ['Z']],
  plus: [['M', 0.64999, 0], ['L', 0.35003, 0], ['L', 0.35003, 0.35003], ['L', 0, 0.35003], ['L', 0, 0.65002], ['L', 0.35003, 0.65002], ['L', 0.35003, 1], ['L', 0.65002, 1], ['L', 0.65002, 0.65004], ['L', 1, 0.65004], ['L', 1, 0.35003], ['L', 0.64999, 0.35003], ['Z']],
  semi_circle: [['M', 0.17157, 0], ['A', 0.58571, 0.58572, 0, 0, 0, 1, 0.82844], ['Z']],
  star: [['M', 0.50001, 0], ['L', 0.65451, 0.32918], ['L', 1, 0.38196], ['L', 0.75008, 0.63819], ['L', 0.8091, 1], ['L', 0.50001, 0.82911], ['L', 0.19099, 0.99993], ['L', 0.25, 0.63812], ['L', 0, 0.38196], ['L', 0.34549, 0.32918], ['Z']],
  trapezium: [['M', 0.13377, 0], ['L', 0, 1], ['L', 1, 1], ['L', 0.86623, 0], ['Z']],
  triangle: [['M', 0.50001, 0], ['L', 0, 0.99992], ['L', 1, 0.99992], ['Z']],
};

/** The outline for a crop mask URL, or null for no mask, `square` or an unknown file. */
export function getMaskShape(maskUrl) {
  if (!maskUrl) {
    return null;
  }

  const match = String(maskUrl).match(/\/([a-z_]+)\.svg(?:[?#].*)?$/i);
  return match ? MASK_SHAPES[match[1].toLowerCase()] || null : null;
}

const round = (value) => Math.round(value * 100) / 100;

function scaleShape(shape, width, height) {
  return shape.map(([command, ...args]) => {
    if (command === 'A') {
      const [rx, ry, rotation, largeArc, sweep, x, y] = args;
      return `A${round(rx * width)} ${round(ry * height)} ${rotation} ${largeArc} ${sweep} ${round(x * width)} ${round(y * height)}`;
    }

    return command + args.map((value, i) => round(i % 2 ? value * height : value * width)).join(' ');
  }).join(' ');
}

function parseRadius(radius) {
  return {
    topLeft: parseInt(radius?.topLeft) || 0,
    topRight: parseInt(radius?.topRight) || 0,
    bottomRight: parseInt(radius?.bottomRight) || 0,
    bottomLeft: parseInt(radius?.bottomLeft) || 0,
  };
}

function roundedRectPath(width, height, corners) {
  const { topLeft, topRight, bottomRight, bottomLeft } = corners;
  const fit = (length, a, b) => (a + b > length ? length / (a + b) : 1);
  const f = Math.min(
    fit(width, topLeft, topRight),
    fit(width, bottomLeft, bottomRight),
    fit(height, topLeft, bottomLeft),
    fit(height, topRight, bottomRight)
  );
  const [tl, tr, br, bl] = [topLeft, topRight, bottomRight, bottomLeft].map(r => round(r * f));
  const w = round(width);
  const h = round(height);

  return `M${tl} 0 H${round(w - tr)} A${tr} ${tr} 0 0 1 ${w} ${tr} V${round(h - br)} A${br} ${br} 0 0 1 ${round(w - br)} ${h} H${bl} A${bl} ${bl} 0 0 1 0 ${round(h - bl)} V${tl} A${tl} ${tl} 0 0 1 ${tl} 0 Z`;
}

export function getMaskShapeGeometry(shape, width, height, radius) {
  const corners = parseRadius(radius);
  const shapePath = scaleShape(shape, width, height);
  const rounded = corners.topLeft || corners.topRight || corners.bottomRight || corners.bottomLeft;
  const box = rounded ? roundedRectPath(width, height, corners) : null;

  return {
    shape: shapePath,
    box,
    stroke: box ? `${shapePath} ${box}` : shapePath,
    radius: rounded ? `${corners.topLeft}px ${corners.topRight}px ${corners.bottomRight}px ${corners.bottomLeft}px` : undefined,
  };
}

const toRgba = (color) => `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;

/** The border colour as CSS, or null when there is nothing to draw. */
export function getMaskShapeBorderColor(border) {
  const color = border?.color;
  return color && color.r !== undefined ? toRgba(color) : null;
}

/** `properties.shadow` in pixels, or null when it is off (same defaults as getBoxShadow). */
export function getMaskShapeShadow(shadow) {
  if (!shadow || !shadow.show) {
    return null;
  }

  const color = shadow.color && shadow.color.r !== undefined ? shadow.color : { r: 255, g: 255, b: 255, a: 1 };

  return {
    x: parseFloat(shadow.axes?.x) || 0,
    y: parseFloat(shadow.axes?.y) || 0,
    blur: Math.max(0, parseFloat(shadow.axes?.b) || 0),
    color: toRgba(color),
  };
}

const MaskShapeShadow = ({ id, geometry, width, height, shadow }) => {
  const pad = Math.abs(shadow.x) + Math.abs(shadow.y) + shadow.blur * 2 + 2;
  const region = { x: -pad, y: -pad, width: width + pad * 2, height: height + pad * 2 };
  const boxClip = geometry.box ? `url(#${id}-box)` : undefined;

  return (
    <svg
      className={styles.maskShapeShadow}
      viewBox={`0 0 ${round(width)} ${round(height)}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {geometry.box &&
          <clipPath id={`${id}-box`} clipPathUnits="userSpaceOnUse">
            <path d={geometry.box} />
          </clipPath>
        }
        {shadow.blur > 0 &&
          <filter id={`${id}-blur`} filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB" {...region}>
            <feGaussianBlur stdDeviation={shadow.blur / 2} />
          </filter>
        }
        <mask id={`${id}-outside`} maskUnits="userSpaceOnUse" {...region}>
          <rect {...region} fill="white" />
          <path d={geometry.shape} fill="black" clipPath={boxClip} />
        </mask>
      </defs>
      <g mask={`url(#${id}-outside)`}>
        <g filter={shadow.blur > 0 ? `url(#${id}-blur)` : undefined}>
          <g transform={`translate(${shadow.x} ${shadow.y})`}>
            <path d={geometry.shape} fill={shadow.color} clipPath={boxClip} />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const MaskShapeLayer = ({ mask, shape, border, shadow, width, height, hideDecorations, children }) => {
  const ref = React.useRef(null);
  const id = `mask-shape-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [size, setSize] = React.useState({ width, height });

  React.useEffect(() => {
    const element = ref.current;
    if (!element || typeof window === 'undefined' || !window.ResizeObserver) {
      return;
    }

    const observer = new window.ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Synchronous, so the border is redrawn for the new size in the same
      // frame as a live resize instead of trailing it by one.
      flushSync(() => {
        setSize(prev => (Math.abs(prev.width - width) < 0.5 && Math.abs(prev.height - height) < 0.5) ? prev : { width, height });
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const measured = size.width > 0 && size.height > 0;
  const geometry = measured ? getMaskShapeGeometry(shape, size.width, size.height, border?.radius) : null;
  const strokeWidth = parseFloat(border?.width) || 0;
  const strokeColor = getMaskShapeBorderColor(border);
  const shadowSpec = getMaskShapeShadow(shadow);
  // The image is clipped by the mask file itself, which is relative to the
  // box and so follows a live resize exactly; a pixel clip-path would lag a
  // frame behind and pull away from the border. The radius clip sits on this
  // same layer, not a parent: Safari leaks a hairline of the image along a
  // rounded overflow clip set on the parent of a masked element.
  const layerStyle = {
    maskImage: `url(${mask})`,
    WebkitMaskImage: `url(${mask})`,
    maskSize: '100% 100%',
    WebkitMaskSize: '100% 100%',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    borderRadius: geometry?.radius,
    overflow: geometry?.radius ? 'hidden' : undefined
  };

  return (
    <>
      {geometry && shadowSpec && !hideDecorations &&
        <MaskShapeShadow id={id} geometry={geometry} width={size.width} height={size.height} shadow={shadowSpec} />
      }
      <div ref={ref} className={styles.maskLayer} style={layerStyle}>
        {children}
        {geometry && strokeWidth > 0 && strokeColor && !hideDecorations &&
          <svg
            className={styles.maskShapeBorder}
            viewBox={`0 0 ${round(size.width)} ${round(size.height)}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path d={geometry.stroke} fill="none" stroke={strokeColor} strokeWidth={strokeWidth * 2} strokeLinejoin="miter" />
          </svg>
        }
      </div>
    </>
  );
};
